import React, { useEffect, useState } from "react";
import api from "@/api";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Plus, Zap } from "lucide-react";

function RecCard({ r, onAdd }) {
  const [amount, setAmount] = useState("1000");
  const amt = parseFloat(amount) || 0;
  const shares = r.price ? amt / r.price : 0;
  const annualIncome = shares * (r.dividend_per_share || 0);
  const monthlyIncome = annualIncome / 12;

  return (
    <div
      data-testid={`recommendation-${r.ticker}`}
      className="card p-4 flex flex-col justify-between hover:border-slate-600 transition-colors"
      style={{ borderColor: "var(--border)" }}
    >
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold text-base">{r.ticker}</span>
          {r.live && (
            <span className="inline-flex items-center gap-1 text-[9px]" style={{ color: "var(--gain)" }}>
              <Zap size={10} /> LIVE
            </span>
          )}
        </div>
        <p className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>
          {r.name}
        </p>
        <p className="text-[10px] mt-1 uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
          {r.category}
        </p>
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <p className="font-serif text-3xl leading-none" style={{ color: "var(--gain)" }}>
            {fmtPct(r.yield_pct)}
          </p>
          <p className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>
            {fmtCurrency(r.dividend_per_share)}/sh · {fmtCurrency(r.price)}
          </p>
        </div>
        <button
          data-testid={`add-recommendation-${r.ticker}`}
          className="btn-secondary !p-2"
          onClick={() => onAdd({ ticker: r.ticker, price: r.price })}
          title={`Add ${r.ticker} to portfolio`}
        >
          <Plus size={14} />
        </button>
      </div>

      {/* YoC Simulator */}
      <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
        <p className="label-xs mb-2">Simulate investment</p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span
              className="absolute left-2 top-1/2 -translate-y-1/2 text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              $
            </span>
            <input
              data-testid={`sim-input-${r.ticker}`}
              className="input-base !py-1.5 !pl-5 !text-xs"
              type="number"
              min="0"
              step="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1000"
            />
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
          <div>
            <p style={{ color: "var(--text-secondary)" }}>Shares</p>
            <p className="font-mono" data-testid={`sim-shares-${r.ticker}`}>
              {shares.toFixed(2)}
            </p>
          </div>
          <div>
            <p style={{ color: "var(--text-secondary)" }}>/ month</p>
            <p className="font-mono" style={{ color: "var(--gain)" }} data-testid={`sim-monthly-${r.ticker}`}>
              {fmtCurrency(monthlyIncome)}
            </p>
          </div>
          <div>
            <p style={{ color: "var(--text-secondary)" }}>/ year</p>
            <p className="font-mono" style={{ color: "var(--gain)" }} data-testid={`sim-annual-${r.ticker}`}>
              {fmtCurrency(annualIncome)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Recommendations({ onAdd }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/recommendations")
      .then((r) => setRecs(r.data.recommendations || []))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="recommendations-section">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="label-xs mb-1">Discover</p>
          <h2 className="font-serif text-2xl">High-yield picks</h2>
        </div>
        <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
          Curated · sorted by yield · type an amount to simulate income
        </p>
      </div>

      {loading ? (
        <div className="card text-xs" style={{ color: "var(--text-secondary)" }}>
          Loading picks<span className="blink">█</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {recs.map((r) => (
            <RecCard key={r.ticker} r={r} onAdd={onAdd} />
          ))}
        </div>
      )}
    </section>
  );
}
